import { ActionIcon, Box, TextInput } from "@mantine/core"
import { MinusIcon, PlusIcon } from "@phosphor-icons/react"
import React from "react"
import { useTranslation } from "react-i18next"
import classes from "./catalog.module.css"

type Props = {
  value: number
  onChange: (value: number) => void
  min?: number
  /** Upper bound (stock); omit for unlimited. */
  max?: number
  disabled?: boolean
  label: string
  /** Extra keyboard handling (the table moves to the next row on Enter). */
  onEnter?: () => void
  inputRef?: React.Ref<HTMLInputElement>
  "data-qty-row"?: number
}

/** − [n] + with a typed value. ArrowUp / ArrowDown change it by one; Enter commits. */
export function QtyStepper({ value, onChange, min = 1, max, disabled = false, label, onEnter, inputRef, ...rest }: Props) {
  const { t } = useTranslation("order")
  const [draft, setDraft] = React.useState(String(value))

  React.useEffect(() => setDraft(String(value)), [value])

  const clamp = (n: number) => {
    const upper = typeof max === "number" ? Math.min(n, max) : n
    return Math.max(upper, min)
  }

  const commit = (raw: string) => {
    const n = parseInt(raw, 10)
    if (Number.isNaN(n)) {
      setDraft(String(value))
      return
    }
    const next = clamp(n)
    setDraft(String(next))
    if (next !== value) onChange(next)
  }

  const step = (delta: number) => {
    const next = clamp(value + delta)
    if (next !== value) onChange(next)
  }

  return (
    <Box className={classes.stepper} data-disabled={disabled || undefined} onClick={(e) => e.stopPropagation()}>
      <ActionIcon
        variant="subtle"
        color="dark"
        size={36}
        aria-label={t("productList.decreaseQty")}
        disabled={disabled || value <= min}
        onClick={() => step(-1)}
      >
        <MinusIcon size={14} />
      </ActionIcon>
      <TextInput
        ref={inputRef}
        variant="unstyled"
        aria-label={label}
        disabled={disabled}
        value={draft}
        inputMode="numeric"
        classNames={{ root: classes.stepperInput, input: classes.stepperInput }}
        data-qty-row={rest["data-qty-row"]}
        onChange={(e) => setDraft(e.currentTarget.value.replace(/[^\d]/g, ""))}
        onBlur={(e) => commit(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            commit(e.currentTarget.value)
            onEnter?.()
          } else if (e.key === "ArrowUp") {
            e.preventDefault()
            step(1)
          } else if (e.key === "ArrowDown") {
            e.preventDefault()
            step(-1)
          }
        }}
      />
      <ActionIcon
        variant="subtle"
        color="dark"
        size={36}
        aria-label={t("productList.increaseQty")}
        disabled={disabled || (typeof max === "number" && value >= max)}
        onClick={() => step(1)}
      >
        <PlusIcon size={14} />
      </ActionIcon>
    </Box>
  )
}
